import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ChevronLeft, Send, MessageCircle, Loader2, User, Building2, Check, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const MessagesPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("client");
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [actingRx, setActingRx] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const messagesEndRef = useRef(null);

  const locale = i18n.language === "en" ? "en-US" : "fr-FR";

  useEffect(() => {
    fetchConversations();
    const conversationInterval = setInterval(() => {
      fetchConversations({ silent: true });
    }, 10000);
    return () => clearInterval(conversationInterval);
  }, []);

  useEffect(() => {
    if (conversationId) {
      fetchMessages(conversationId);
      const messageInterval = setInterval(() => {
        fetchMessages(conversationId, { silent: true });
      }, 3000);
      return () => clearInterval(messageInterval);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async ({ silent = false } = {}) => {
    try {
      const response = await apiClient.get("/messages/conversations");
      setConversations(response.data);
    } catch (error) {
      if (!silent) toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId, { silent = false } = {}) => {
    try {
      const response = await apiClient.get(`/messages/conversation/${convId}`);
      setSelectedConversation(response.data.conversation);
      setMessages(response.data.messages);
    } catch (error) {
      if (!silent) toastApiError(error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const recipientId = user.role === "client"
        ? (selectedConversation.kind === "doctor_client"
            ? selectedConversation.doctor_id
            : selectedConversation.pharmacy_id)
        : selectedConversation.client_id;

      await apiClient.post("/messages/send", {
        recipient_id: recipientId,
        content: newMessage,
        prescription_id: selectedConversation.prescription_id || undefined,
      });

      setNewMessage("");
      fetchMessages(selectedConversation.id);
    } catch (error) {
      toastApiError(error);
    } finally {
      setSending(false);
    }
  };

  const prescription = selectedConversation?.prescription;

  const prescriptionStatusLabel = (status) => {
    if (status === "on_hold") return t("prescriptionPending");
    if (status === "validated") return t("prescriptionValidated");
    if (status === "rejected") return t("prescriptionRejected");
    return status;
  };

  const canDecidePrescription = user?.role === "doctor"
    && selectedConversation?.kind === "doctor_client"
    && prescription?.status === "on_hold";

  const decidePrescription = async (action, note = "") => {
    if (!prescription?.id) return;
    setActingRx(true);
    try {
      const res = await apiClient.put(`/prescriptions/${prescription.id}/${action}`, { note: note || null });
      toast.success(res.data.message || t("prescriptionUpdated"));
      setRejectOpen(false);
      setRejectNote("");
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toastApiError(error);
    } finally {
      setActingRx(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  };

  const isOwnMessage = (msg) => {
    if (user.role === "client") {
      return msg.sender_role === "client";
    } else if (user.role === "pharmacy") {
      return msg.sender_role === "pharmacy";
    } else if (user.role === "doctor") {
      return msg.sender_role === "doctor";
    }
    return msg.sender_id === user.id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Button variant="ghost" size="icon" onClick={() => navigate(user.role === "pharmacy" ? "/dashboard" : user.role === "doctor" ? "/doctor/prescriptions" : "/")}>
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Link to="/">
              <img src={LOGO_URL} alt="OMU" className="h-12 w-auto" />
            </Link>
            <h1 className="font-heading font-semibold text-slate-900">{t("messagesTitle")}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 w-full">
        <div className="grid md:grid-cols-3 gap-4 min-h-[calc(100dvh-8rem)] md:h-[calc(100dvh-8rem)]">
          <div className="md:col-span-1 bg-white rounded-2xl shadow-soft overflow-hidden flex flex-col">
            <div className="p-4 border-b">
              <h2 className="font-heading font-semibold">{t("conversations")}</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <MessageCircle className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  <p>{t("noConversations")}</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`p-4 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedConversation?.id === conv.id ? "bg-omu-blue-50" : ""
                    }`}
                    onClick={() => navigate(`/messages/${conv.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-omu-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {user.role === "client" ? (
                          <Building2 className="h-5 w-5 text-omu-blue-500" />
                        ) : (
                          <User className="h-5 w-5 text-omu-blue-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {conv.participant?.entity_name || conv.participant?.name || t("defaultUser")}
                        </p>
                        {conv.last_message && (
                          <p className="text-sm text-slate-500 truncate">
                            {conv.last_message.content}
                          </p>
                        )}
                      </div>
                      {((user.role === "client" && conv.unread_client > 0) ||
                        (user.role === "pharmacy" && conv.unread_pharmacy > 0) ||
                        (user.role === "doctor" && conv.unread_doctor > 0)) && (
                        <span className="bg-omu-blue-500 text-white text-xs px-2 py-1 rounded-full flex-shrink-0">
                          {user.role === "client" ? conv.unread_client : user.role === "doctor" ? conv.unread_doctor : conv.unread_pharmacy}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="md:col-span-2 bg-white rounded-2xl shadow-soft overflow-hidden flex flex-col">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b bg-slate-50 flex-shrink-0 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-omu-blue-100 rounded-full flex items-center justify-center">
                      {user.role === "client" ? (
                        <Building2 className="h-5 w-5 text-omu-blue-500" />
                      ) : (
                        <User className="h-5 w-5 text-omu-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">
                        {conversations.find(c => c.id === selectedConversation.id)?.participant?.entity_name ||
                         conversations.find(c => c.id === selectedConversation.id)?.participant?.name ||
                         t("defaultUser")}
                      </p>
                      {user.role === "pharmacy" && conversations.find(c => c.id === selectedConversation.id)?.participant?.phone && (
                        <p className="text-sm text-slate-500">
                          {conversations.find(c => c.id === selectedConversation.id)?.participant?.phone}
                        </p>
                      )}
                      {prescription && (
                        <p className="text-xs text-slate-500">
                          {t("prescriptionStatus", { status: prescriptionStatusLabel(prescription.status) })}
                        </p>
                      )}
                    </div>
                  </div>
                  {canDecidePrescription && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700" disabled={actingRx} onClick={() => decidePrescription("validate")}>
                        <Check className="h-4 w-4 mr-1" /> {t("validatePrescription")}
                      </Button>
                      <Button size="sm" variant="destructive" className="rounded-full" disabled={actingRx} onClick={() => setRejectOpen(true)}>
                        <X className="h-4 w-4 mr-1" /> {t("reject")}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center text-slate-400 py-10">
                      <p>{t("noMessages")}</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage(msg) ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            isOwnMessage(msg)
                              ? "bg-omu-blue-500 text-white"
                              : "bg-slate-100 text-slate-900"
                          }`}
                        >
                          <p className="break-words">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            isOwnMessage(msg) ? "text-blue-100" : "text-slate-400"
                          }`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t flex-shrink-0">
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder={t("messagePlaceholder")}
                      className="flex-1 rounded-full h-12"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    />
                    <Button
                      className="rounded-full bg-omu-blue-500 hover:bg-omu-blue-600 h-12 w-12"
                      onClick={sendMessage}
                      disabled={sending || !newMessage.trim()}
                    >
                      {sending ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageCircle className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <p>{t("selectConversation")}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("rejectPrescriptionTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{t("rejectPrescriptionHint")}</p>
          <Textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder={t("rejectReasonPlaceholder")}
            className="rounded-xl"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>{t("cancel")}</Button>
            <Button variant="destructive" disabled={actingRx} onClick={() => decidePrescription("reject", rejectNote)}>
              {t("confirmReject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
